<script setup>
import {
  defineProps,
  computed,
  ref,
  watch,
  defineEmits,
  defineExpose,
  onUnmounted,
  onMounted
} from 'vue';
import { useStore } from 'vuex';
import { MsPopoverPosition } from '@/lib/vueFinalPopover/VueFinalPopover.vue';
import { useTimeout } from '@/setup/commonComposition.js';

const emit = defineEmits(['onClose', 'onScroll', 'onMouseLeave']);

const props = defineProps({
  /**
   * Target element cần truyền vào để xác định vị trí xuất hiện popover trên DOM.
   */
  target: {
    type: HTMLDivElement,
    default: null,
  },
  /**
   * Vị trí xuất hiện.
   * x: vị trí đặt popover vào trục x của target element left, center, right
   * y: vị trí đặt popover vào trục y của target element top, center, bottom
   * offsetX: margin-left của popover so với trục x
   * offsetY: margin-top của popover so với trục y
   * isOuterX: nếu true tâm của popover sẽ hướng ra ngoài target element theo trục X (Lấy trục Y làm tâm) ngược lại hướng vào trong
   * isOuterY: nếu true tâm của popover sẽ hướng ra ngoài target element theo trục Y (Lấy trục X làm tâm) ngược lại hướng vào trong
   */
  position: {
    type: Object,
    default: () => ({
      x: MsPopoverPosition.Left,
      y: MsPopoverPosition.Bottom,
      offsetX: 0,
      offsetY: 0,
      isOuterX: false,
      isOuterY: true,
    }),
    validator(val) {
      // offsetX chỉ cho phép số
      if (val.offsetX !== undefined && typeof val.offsetX !== 'number') {
        console.warn('offsetX chỉ cho phép số');
        return false;
      }
      // offsetY chỉ cho phép số
      if (val.offsetY !== undefined && typeof val.offsetY !== 'number') {
        console.warn('offsetY chỉ cho phép số');
        return false;
      }
      // Trục X chỉ nhận giá trị left, center, right
      if (
        val.x !== undefined &&
        val.x !== MsPopoverPosition.Left &&
        val.x !== MsPopoverPosition.Right &&
        val.x !== MsPopoverPosition.Center
      ) {
        console.warn('Trục X chỉ nhận giá trị left, center, right');
        return false;
      }
      // Trục Y chỉ nhận giá trị left, center, right
      if (
        val.y !== undefined &&
        val.y !== MsPopoverPosition.Top &&
        val.y !== MsPopoverPosition.Bottom &&
        val.y !== MsPopoverPosition.Center
      ) {
        console.warn('Trục Y chỉ nhận giá trị top, center, bottom');
        return false;
      }
      // isOuterX chỉ nhận kiểu boolean
      if (val.isOuterX !== undefined && typeof val.isOuterX !== 'boolean') {
        console.warn('isOuterX chỉ nhận kiểu boolean');
        return false;
      }
      // isOuterY chỉ nhận kiểu boolean
      if (val.isOuterY !== undefined && typeof val.isOuterY !== 'boolean') {
        console.warn('isOuterY chỉ nhận kiểu boolean');
        return false;
      }

      return true;
    },
  },
  /**
   * Chiều rộng của popover
   */
  width: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều cao của popover
   */
  height: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều cao ảo của popover ko có tác dụng hiển thị
   * Chỉ dùng khi tính vị trí popover
   */
  virtualHeight: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều rộng tối thiểu của popover
   */
  minWidth: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều cao tối thiểu của popover
   */
  minHeight: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều rộng tối đa của popover
   */
  maxWidth: {
    type: Number,
    default: 0,
  },
  /**
   * Chiều cao tối đa của popover
   */
  maxHeight: {
    type: Number,
    default: 0,
  },
  /**
   * Trạng thái ẩn hiện popover
   */
  isActive: {
    type: Boolean,
    default: false,
  },
  /**
   * Transition cho popover
   */
  transition: {
    type: String,
    default: 'ms-fade',
  },
  /**
   * z-index
   */
  zIndex: {
    type: Number,
    default: 10,
  },
  /**
   * custom class
   */
  customClass: {
    type: String,
    default: '',
  },
  /**
   * custom style
   */
  customStyle: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Có mũi tên hay ko
   */
  hasArrow: {
    type: Boolean,
    default: false,
  },
  /**
   * Custom style của mũi tên
   */
  customStyleArrow: {
    type: Object,
    default: () => ({}),
  },
  /**
   * Element ngoại lệ sẽ ko đóng popover khi nhấn vào các element này
   */
  exceptEls: {
    type: Array,
    default: () => [],
  },
  /**
   * Nếu Element nào có class thuộc class đc liệt kê trong đây thì sẽ ko đóng popover khi nhấn vào element đó
   */
  exceptClasses: {
    type: Array,
    default: () => [],
  },
  /**
   * Có phải tooltip ko
   */
  isTooltip: {
    type: Boolean,
    default: false,
  },
  /**
   * Có đóng khi scroll bên ngoài ko
   */
  isCloseOnScrollOutSide: {
    type: Boolean,
    default: true,
  },
  /**
   * Có phải popover trong tour guide ko
   */
  isInTourGuide: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

/**
 * ID của popover
 */
const popoverId = ref(-1);

/**
 * Ref của popover
 */
const popoverRef = ref(null);

/**
 * Trạng thái ẩn hiện
 */
const isVisible = ref(false);

/**
 * Có cho phép thực hiện transition ko
 */
const isAllowTransition = ref(false);

/**
 * Style của popover
 */
const popoverStyle = ref({
  top: 0,
  left: 0,
});

/**
 * Style của arrow
 */
const arrowStyle = ref({
  top: 0,
  left: 0,
  transform: null,
});

const isLoaded = ref(false);

const { runTimeoutFunc } = useTimeout();

/**
 * Style của container với chiều rộng và chiều cao
 * @author: BMThang(02/11/2022)
 */
const computedPopoverStyle = computed(() => {
  return {
    ...popoverStyle.value,
    zIndex: props.zIndex,
  };
});

/**
 * Style của content với chiều rộng và chiều cao
 * @author: BMThang(02/11/2022)
 */
const computedPopoverContent = computed(() => {
  return {
    width: props.width ? `${props.width}px` : null,
    height: props.height ? `${props.height}px` : null,
    minWidth: props.minWidth ? `${props.minWidth}px` : null,
    minHeight: props.minHeight ? `${props.minHeight}px` : null,
    maxWidth: props.maxWidth ? `${props.maxWidth}px` : null,
    maxHeight: props.maxHeight ? `${props.maxHeight}px` : null,
    opacity: isAllowTransition.value ? '1' : '0',
    ...props.customStyle,
  };
});

/**
 * Style của arrow
 * @author: BMThang(02/11/2022)
 */
const computedPopoverArrow = computed(() => {
  return {
    ...arrowStyle.value,
    ...props.customStyleArrow,
    opacity: isAllowTransition.value ? '1' : '0',
  };
});

/**
 * Xử lý khi bấm ra ngoài popover hoặc ko phải target element thì emit onClose
 * @param {object} e event object
 * @author: BMThang(16/11/2022)
 */
const handleClickOutSise = (e) => {
  if (!popoverRef.value || !props.target) {
    return;
  }
  if (
    !props.target.contains(e.target) &&
    !popoverRef.value.contains(e.target)
  ) {
    // Nếu có các element ngoại lệ thì kiểm tra những element này nếu ko bấm vào các elelemt này thì đóng
    if (props.exceptEls.length) {
      if (props.exceptEls.some((el) => el?.contains(e.target))) {
        return;
      }
    }

    // Nếu có element đc bấm có chứa các class trong exceptClassed thì out
    if (props.exceptClasses.length) {
      if (
        props.exceptClasses.some((elClass) =>
          e.target.classList.contains(
            typeof elClass === 'string' ? elClass : ''
          )
        )
      ) {
        return;
      }
    }

    // Kiểm tra danh sách popover đang mở
    const popoverList = store.state.popover.popoverList;
    let isFounded = false;
    const popoverListCount = popoverList.length;
    for (let index = 0; index < popoverListCount; index++) {
      const currentPopoverId = popoverList[index];
      // Nếu là popover hiện tại thì set isFounded = true và next sang cái tiếp theo
      if (currentPopoverId === popoverId.value) {
        isFounded = true;
        continue;
      }
      // Nếu ko phải popover hiện tại và chưa tìm đc popover hiện tại thì next
      if (currentPopoverId !== popoverId.value && !isFounded) {
        continue;
      }
      // Nếu ko phải popover hiện tại thì kiểm tra xem popover có chứa element đc bấm ko
      // Nếu chứa thì thôi ko đóng popover nữa
      const popoverEl = document.querySelector(
        `#ms-popover-in-component-${currentPopoverId}`
      );
      if (popoverEl?.contains(e.target)) {
        return;
      }
    }

    emit('onClose');
  }
};

/**
 * Xử lý khi scroll ở ngoài popover hoặc ko phải target element thì emit onClose
 * @param {object} e event object
 * @author: BMThang(10/02/2023)
 */
const handleScrollOutSide = (e) => {
  if (!popoverRef.value || !props.target) {
    return;
  }
  if (
    !props.target.contains(e.target) &&
    !popoverRef.value.contains(e.target)
  ) {
    // Nếu có các element ngoại lệ thì kiểm tra những element này nếu ko bấm vào các elelemt này thì đóng
    if (props.exceptEls.length) {
      if (props.exceptEls.some((el) => el?.contains(e.target))) {
        return;
      }
    }

    // Nếu có element đc bấm có chứa các class trong exceptClassed thì out
    if (props.exceptClasses.length) {
      if (
        props.exceptClasses.some((elClass) =>
          e.target.classList.contains(
            typeof elClass === 'string' ? elClass : ''
          )
        )
      ) {
        return;
      }
    }

    // Kiểm tra danh sách popover đang mở
    const popoverList = store.state.popover.popoverList;
    let isFounded = false;
    const popoverListCount = popoverList.length;
    for (let index = 0; index < popoverListCount; index++) {
      const currentPopoverId = popoverList[index];
      // Nếu là popover hiện tại thì set isFounded = true và next sang cái tiếp theo
      if (currentPopoverId === popoverId.value) {
        isFounded = true;
        continue;
      }
      // Nếu ko phải popover hiện tại và chưa tìm đc popover hiện tại thì next
      if (currentPopoverId !== popoverId.value && !isFounded) {
        continue;
      }
      // Nếu ko phải popover hiện tại thì kiểm tra xem popover có chứa element đc bấm ko
      // Nếu chứa thì thôi ko đóng popover nữa
      const popoverEl = document.querySelector(
        `#ms-popover-in-component-${currentPopoverId}`
      );
      if (popoverEl?.contains(e.target)) {
        return;
      }
    }

    emit('onClose');
  }
}

/**
 * Tính style của container để đặt đúng vị trí theo target element và position
 * @author: BMThang(02/11/2022)
 */
const calculatePopoverStyle = (popoverDimentions = null) => {
  const { position } = props;
  // Lấy target element
  const target = props.target;

  // Nếu có target element và ref của content thì mới thực hiện
  if (target) {
    try {
      let { top, left, width, height } = target.getBoundingClientRect();
      let { width: popoverWidth, height: popoverHeight } = popoverDimentions
        ? popoverDimentions
        : popoverRef.value.getBoundingClientRect();

      // Nếu có chiều cao ảo thì lấy height theo giá trị này
      if (props.virtualHeight) {
        popoverHeight = props.virtualHeight;
      }

      const targetTop = top;
      const targetLeft = left;

      let positionX =
        position.x === undefined ? MsPopoverPosition.Left : position.x;
      let positionY =
        position.y === undefined ? MsPopoverPosition.Bottom : position.y;
      let positionOffsetX =
        position.offsetX === undefined ? 0 : position.offsetX;
      let positionOffsetY =
        position.offsetY === undefined ? 0 : position.offsetY;
      let positionIsOuterX =
        position.isOuterX === undefined ? false : position.isOuterX;
      let positionIsOuterY =
        position.isOuterY === undefined ? true : position.isOuterY;

      if (positionX === MsPopoverPosition.Left) {
        if (positionIsOuterX) {
          left -= popoverWidth;
          // Nếu ra khỏi chiều rộng window thì đổi chiều
          if (left < 0) {
            left += popoverWidth;
            left += width;
          }
        } else {
          // Nếu ra khỏi chiều rộng window thì đổi chiều
          if (left + popoverWidth > window.innerWidth) {
            left -= popoverWidth;
            left += width;
          }
        }
      }

      if (positionX === MsPopoverPosition.Right) {
        if (positionIsOuterX) {
          left += width;
          // Nếu ra khỏi chiều rộng window thì đổi chiều
          if (left + popoverWidth > window.innerWidth) {
            left -= width;
            left -= popoverWidth;
          }
        } else {
          left += width - popoverWidth;
          // Nếu ra khỏi chiều rộng window thì đổi chiều
          if (left < 0) {
            left += popoverWidth;
            left -= width;
          }
        }
      }

      if (positionX === MsPopoverPosition.Center) {
        left += parseInt(width / 2) - parseInt(popoverWidth / 2);
        if (left < 0) {
          left = 0;
        } else if (left + popoverWidth > window.innerWidth) {
          left -= left + popoverWidth - window.innerWidth;
        }
      }

      if (positionY === MsPopoverPosition.Bottom) {
        if (positionIsOuterY) {
          top += height;
          // Nếu popover vượt ra khỏi chiều dài của window thì đổi vị trí của nó lên trên
          if (top + popoverHeight + positionOffsetY > window.innerHeight) {
            top -= height;
            top -= popoverHeight;
          }
          // Nếu cao hơn window thì đẩy xuống
          if (top + positionOffsetY < 0) {
            top += (Math.abs(top) - positionOffsetY);
          }
        } else {
          top -= popoverHeight;
          top += height;
          // Nếu popover vượt ra khỏi chiều dài của window thì đổi vị trí của nó xuống dưới
          if (top + positionOffsetY < 0) {
            top += popoverHeight;
            top -= height;
          }
          // Nếu vẫn cao hơn thì đẩy lên lên
          if (top + popoverHeight + positionOffsetY > window.innerHeight) {
            top -= (top + popoverHeight - window.innerHeight);
          }
        }
      }

      if (positionY === MsPopoverPosition.Top) {
        if (positionIsOuterY) {
          top -= popoverHeight;
          // Nếu popover vượt ra khỏi chiều dài của window thì đổi vị trí của nó xuống dưới
          if (top + positionOffsetY < 0) {
            top += popoverHeight;
            top += height;
          }
          // Nếu vẫn cao hơn thì đẩy lên lên
          if (top + popoverHeight + positionOffsetY > window.innerHeight) {
            top -= (top + popoverHeight - window.innerHeight);
          }
        } else {
          // Nếu popover vượt ra khỏi chiều dài của window thì đổi vị trí của nó lên trên
          if (top + popoverHeight + positionOffsetY > window.innerHeight) {
            top -= popoverHeight;
            top += height;
          }
          // Nếu cao hơn window thì đẩy xuống
          if (top + positionOffsetY < 0) {
            top += (Math.abs(top) - positionOffsetY);
          }
        }
      }

      if (positionY === MsPopoverPosition.Center) {
        top += parseInt(height / 2) - parseInt(popoverHeight / 2);
        if (top < 0) {
          top = 0;
        } else if (top + popoverHeight > window.innerHeight) {
          top -= top + popoverHeight - window.innerHeight;
        }
      }

      top += positionOffsetY && !isNaN(positionOffsetY) ? positionOffsetY : 0;
      left += positionOffsetX && !isNaN(positionOffsetX) ? positionOffsetX : 0;

      // Nếu có mũi tên thì xét vị trí cho mũi tên
      if (props.hasArrow) {
        //
        // Nếu popover nằm bên dưới target
        if (
          top >= targetTop + height &&
          left + popoverWidth >= targetLeft &&
          left <= targetLeft + width &&
          positionIsOuterX === false &&
          positionIsOuterY === true
        ) {
          arrowStyle.value.top = '-10px';
          arrowStyle.value.left = `${
            targetLeft + parseInt(width / 2) - 8 - left
          }px`;
          arrowStyle.value.transform = null;
          top += 10;
        }
        // Nếu popover nằm bên trên target
        else if (
          top <= targetTop &&
          left + popoverWidth >= targetLeft &&
          left <= targetLeft + width &&
          positionIsOuterX === false &&
          positionIsOuterY === true
        ) {
          arrowStyle.value.top = '100%';
          arrowStyle.value.left = `${
            targetLeft + parseInt(width / 2) - 8 - left
          }px`;
          arrowStyle.value.transform = 'rotate(180deg)';
          top -= 10;
        }
        // Nếu popover nằm bên phải target
        else if (
          left >= targetLeft + width &&
          top <= targetTop + height &&
          top + popoverHeight >= targetTop &&
          positionIsOuterX === true &&
          positionIsOuterY === false
        ) {
          arrowStyle.value.top = `${
            targetTop + parseInt(height / 2) - 8 - top
          }px`;
          arrowStyle.value.left = '-13px';
          arrowStyle.value.transform = 'rotate(-90deg)';
          left += 12;
        }
        // Nếu popover nằm bên trái target
        else if (
          left + width <= targetLeft &&
          top <= targetTop + height &&
          top + popoverHeight >= targetTop &&
          positionIsOuterX === true &&
          positionIsOuterY === false
        ) {
          arrowStyle.value.top = `${
            targetTop + parseInt(height / 2) - 8 - top
          }px`;
          arrowStyle.value.left = 'calc(100% - 3px)';
          arrowStyle.value.transform = 'rotate(90deg)';
          left -= 12;
        }
      }

      return {
        top: `${top}px`,
        left: `${left}px`,
      };
    } catch {
      return {
        top: 0,
        left: 0,
      };
    }
  }
  return {
    top: 0,
    left: 0,
  };
};

/**
 * Tính vị trí và xét vị trí mới cho popover
 * @author: BMThang(17/11/2022)
 */
const calculatePopoverPosition = (a = null) => {
  popoverStyle.value = calculatePopoverStyle(a);
};

/**
 * Hiện popover
 * @author: BMThang(2/12/2022)
 */
const showPopover = () => {
  isVisible.value = true;
  isAllowTransition.value = false;
  runTimeoutFunc(
    () => {
      // Nếu ko có popover thì ko làm j
      if (!popoverRef.value) {
        return;
      }

      const e = popoverRef.value.getBoundingClientRect();
      isVisible.value = false;

      runTimeoutFunc(
        () => {
          isVisible.value = true;
          isAllowTransition.value = true;
          calculatePopoverPosition(e);

          // Thêm vào list
          if (store.state.popover.popoverList.length) {
            popoverId.value = Math.max(...store.state.popover.popoverList) + 1;
          } else {
            popoverId.value = 0;
          }
          store.commit('popover/addPopover', popoverId.value);
        },
        1,
        'visibleTimeOut'
      );
    },
    1,
    'transitionTimeOut'
  );
};

/**
 * Emit event khi scroll
 * @param {object} e event object
 * @author: BMThang(29/11/2022)
 */
const handleScroll = (e) => {
  emit('onScroll', e);
};

/**
 * Emit sự kiện onMouseLeave
 * @param {object} e event object
 * @author: BMThang(29/11/2022)
 */
const handleMouseLeave = (e) => {
  emit('onMouseLeave', e);
};

/**
 * Khi active thì tính toán vị trí của popover
 * @author: BMThang(17/11/2022)
 */
watch(
  () => props.isActive,
  (newVal) => {
    if (newVal) {
      window.addEventListener('click', handleClickOutSise);
      if (props.isCloseOnScrollOutSide) {
        document.addEventListener('scroll', handleScrollOutSide, true);
      }
      showPopover();
      return;
    }

    // Xóa event click out side
    window.removeEventListener('click', handleClickOutSise);
    if (props.isCloseOnScrollOutSide) {
      document.removeEventListener('scroll', handleScrollOutSide, true);
    }

    // Xóa khỏi list popover
    runTimeoutFunc(
      () => {
        store.commit('popover/removePopover', {
          popoverId: popoverId.value,
          isTooltip: props.isTooltip,
        });
      },
      1,
      'removePopoverTimeout'
    );
  },
  {
    flush: 'post',
  }
);

onMounted(() => {
  isLoaded.value = true;
});

onUnmounted(() => {
  // Xóa event click out side
  window.removeEventListener('click', handleClickOutSise);
  if (props.isCloseOnScrollOutSide) {
    document.removeEventListener('scroll', handleScrollOutSide, true);
  }
})

defineExpose({
  calculatePopoverPosition,
  popoverRef,
});
</script>

<template>
  <teleport v-if="isLoaded" :to="isInTourGuide ? '#ms-tour-guide' : '#ms-popover-in-component-container'">
    <transition :name="isAllowTransition ? transition : ''">
      <div
        v-if="isActive && isVisible"
        :id="`ms-popover-in-component-${popoverId}`"
        ref="popoverRef"
        :class="[
          'ms-popover-in-component',
          customClass
        ]"
        :style="computedPopoverStyle"
        @mouseleave="handleMouseLeave"
      >
        <!-- Content -->
        <div
          class="ms-popover-in-component-content"
          :style="computedPopoverContent"
          @scroll.stop="handleScroll"
        >
          <slot />
        </div>

        <!-- Arrow -->
        <div
          v-if="hasArrow"
          class="ms-popover-in-component-arrow"
          :style="computedPopoverArrow"
        />
      </div>
    </transition>
  </teleport>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/_variables.scss';
@import '@/assets/scss/transitions.scss';
.ms-popover-in-component {
  position: fixed;
  .ms-popover-in-component-arrow {
    position: absolute;
    top: 20px;
    left: 0;
    border-bottom: 10px solid var(--white);
    border-right: 8px solid transparent;
    border-left: 8px solid transparent;
  }
  .ms-popover-in-component-content {
    background-color: var(--white);
    box-shadow: 0 0 16px 0 rgba(23, 27, 42, 0.16);
    padding: 8px;
    border-radius: 4px;
    overflow: auto;
    width: 100%;
    height: 100%;
  }
}
</style>